import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api"
});

// Variable pour éviter les redirections multiples
let isRedirecting = false;

// Fonction pour nettoyer l'authentification
const clearAuthAndRedirect = () => {
  if (isRedirecting) return; // Éviter les redirections multiples
  
  isRedirecting = true;
  localStorage.clear();
  
  toast.error("Session expirée. Redirection vers la page de connexion...", {
    position: "top-right",
    autoClose: 3000,
  });
  
  setTimeout(() => {
    window.location.href = "/login";
  }, 2000);
};

// ⏳ Intercepteur des requêtes
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;

        // Vérifier si le token n'est pas expiré (avec une marge de 5 minutes)
        if (decoded.exp < (now + 300)) {
          clearAuthAndRedirect();
          return Promise.reject(new Error("Token expiré"));
        }

        config.headers.Authorization = `Bearer ${token}`;
      } catch (e) {
        console.error("Erreur lors du décodage du token:", e);
        clearAuthAndRedirect();
        return Promise.reject(new Error("Token invalide"));
      }
    }

    return config;
  },
  (error) => {
    console.error("Erreur dans l'intercepteur de requête:", error);
    return Promise.reject(error);
  }
);

// 🚫 Intercepteur des réponses
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("Erreur de réponse:", error.response?.status, error.response?.data);
    
    if (error.response?.status === 401) {
      // Ne pas rediriger sur échec de login (identifiants incorrects)
      const url = error.config?.url || "";
      const isLoginRequest = url.includes("/auth/login");
      if (!isLoginRequest && !isRedirecting) {
        clearAuthAndRedirect();
      }
    } else if (error.response?.status === 403) {
      // Erreur d'autorisation
      toast.error("Accès refusé. Vous n'avez pas les permissions nécessaires.", {
        position: "top-right",
        autoClose: 4000,
      });
    } else if (error.response?.status >= 500) {
      // Erreur serveur
      toast.error("Erreur serveur. Veuillez réessayer plus tard.", {
        position: "top-right",
        autoClose: 4000,
      });
    } else if (error.response?.status >= 400) {
      // Erreur client
      const message = error.response?.data?.detail || "Une erreur est survenue";
      toast.error(message, {
        position: "top-right",
        autoClose: 4000,
      });
    } else if (error.code === "NETWORK_ERROR" || error.code === "ERR_NETWORK") {
      // Erreur de réseau
      toast.error("Erreur de connexion. Vérifiez votre connexion internet.", {
        position: "top-right",
        autoClose: 4000,
      });
    }
    
    return Promise.reject(error);
  }
);

export default instance;

